import { useState } from "react";
import { Trash, Plus, PencilSimple, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { ALL_TSP_FUNDS, fundColor } from "@/lib/finance";
import { usd, num, pct, changeClass } from "@/lib/format";

const ACCT_LABEL = { tsp: "TSP", trad_ira: "Traditional IRA", roth_ira: "Roth IRA", other: "Other" };

function AddRow({ accounts, onAdded }) {
  const [f, setF] = useState({ account: accounts[0], symbol: accounts[0] === "tsp" ? "C" : "", units: "" });
  const add = async () => {
    if (!f.symbol || f.units === "") return toast.error("Enter a fund/ticker and units");
    try {
      await api.post("/holdings", { ...f, units: Number(f.units) });
      setF({ ...f, units: "", symbol: f.account === "tsp" ? f.symbol : "" });
      onAdded();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const tsp = f.account === "tsp";
  return (
    <div className="flex flex-wrap gap-2 items-center pt-4" data-testid={`add-holding-row-${accounts[0]}`}>
      {accounts.length > 1 && (
        <Select value={f.account} onValueChange={(v) => setF({ ...f, account: v, symbol: "" })}>
          <SelectTrigger className="w-40" data-testid="add-holding-account-select"><SelectValue /></SelectTrigger>
          <SelectContent>{accounts.map((a) => <SelectItem key={a} value={a}>{ACCT_LABEL[a]}</SelectItem>)}</SelectContent>
        </Select>
      )}
      {tsp ? (
        <Select value={f.symbol} onValueChange={(v) => setF({ ...f, symbol: v })}>
          <SelectTrigger className="w-36" data-testid="add-holding-fund-select"><SelectValue placeholder="Fund" /></SelectTrigger>
          <SelectContent>{ALL_TSP_FUNDS.map((x) => <SelectItem key={x} value={x}>{x.length === 1 ? `${x} Fund` : x}</SelectItem>)}</SelectContent>
        </Select>
      ) : (
        <Input className="w-32 uppercase" placeholder="Ticker (VTI)" value={f.symbol} onChange={(e) => setF({ ...f, symbol: e.target.value })} data-testid="add-holding-ticker-input" />
      )}
      <Input className="w-36 font-mono-num" type="number" step="any" min="0" placeholder={tsp ? "Shares (units)" : "Shares"} value={f.units} onChange={(e) => setF({ ...f, units: e.target.value })} data-testid="add-holding-units-input" />
      <Button size="sm" onClick={add} className="gap-1" data-testid="add-holding-submit"><Plus size={14} /> Add</Button>
    </div>
  );
}

function UnitsCell({ h, update, edit, close }) {
  const [v, setV] = useState(String(h.units));
  const cancel = () => { setV(String(h.units)); close(); };
  const save = async () => { if (v === "" || Number(v) < 0) return toast.error("Enter shares of 0 or more"); if (Number(v) !== h.units) await update(h, v); close(); };
  if (!edit) return <span className="font-mono-num" data-testid={`holding-units-${h.id}`}>{num(h.units, 4)}</span>;
  return (
    <span className="inline-flex items-center gap-1">
      <input autoFocus value={v} type="number" step="any" min="0" onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") cancel(); }}
        className="w-28 bg-white dark:bg-slate-900 text-right font-mono-num rounded px-1.5 py-0.5 ring-1 ring-cyan-500 focus:outline-none" data-testid={`holding-units-input-${h.id}`} />
      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={save} aria-label="Save shares" data-testid={`holding-save-${h.id}`}><Check size={15} className="text-emerald-600" /></Button>
      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={cancel} aria-label="Cancel" data-testid={`holding-cancel-${h.id}`}><X size={15} /></Button>
    </span>
  );
}

export function HoldingsTable({ holdings = [], accounts, onChange }) {
  const rows = holdings.filter((h) => accounts.includes(h.account));
  const total = rows.reduce((s, h) => s + h.value, 0);
  const [editing, setEditing] = useState(null);
  const del = async (id) => {
    await api.delete(`/holdings/${id}`);
    onChange();
  };
  const update = async (h, units) => {
    try {
      await api.put(`/holdings/${h.id}`, { account: h.account, symbol: h.symbol, units: Number(units), price: h.price });
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={`holdings-table-${accounts[0]}`}>
          <thead><tr className="text-xs text-muted-foreground border-b border-border">
            <th className="text-left font-medium py-2">Holding</th>{accounts.length > 1 && <th className="text-left font-medium">Account</th>}
            <th className="text-right font-medium">Units</th><th className="text-right font-medium">Price</th><th className="text-right font-medium">1D</th><th className="text-right font-medium">Value</th><th />
          </tr></thead>
          <tbody>
            {rows.map((h) => (
              <tr key={h.id} className="border-b border-border/60" data-testid={`holding-row-${h.id}`}>
                <td className="py-2"><span className="inline-flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: h.account === "tsp" ? fundColor(h.symbol) : "#F43F5E" }} />{h.account === "tsp" && h.symbol.length === 1 ? `${h.symbol} Fund` : h.symbol}</span></td>
                {accounts.length > 1 && <td className="text-xs text-muted-foreground">{ACCT_LABEL[h.account]}</td>}
                <td className="text-right"><UnitsCell key={`${h.id}-${h.units}-${editing === h.id}`} h={h} update={update} edit={editing === h.id} close={() => setEditing(null)} /></td>
                <td className="text-right font-mono-num">${num(h.live_price, 2)}</td>
                <td className={`text-right font-mono-num ${changeClass(h.change_pct)}`}>{pct(h.change_pct)}</td>
                <td className="text-right font-mono-num font-medium" data-testid={`holding-value-${h.id}`}>{usd(h.value, 2)}</td>
                <td className="text-right whitespace-nowrap"><Button variant="ghost" size="icon" onClick={() => setEditing(h.id)} aria-label="Edit shares" data-testid={`holding-edit-${h.id}`}><PencilSimple size={15} /></Button><Button variant="ghost" size="icon" onClick={() => del(h.id)} aria-label="Delete" data-testid={`holding-delete-${h.id}`}><Trash size={15} /></Button></td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={7} className="py-6 text-center text-muted-foreground text-sm">No holdings yet.</td></tr>}
          </tbody>
          {rows.length > 0 && <tfoot><tr><td colSpan={accounts.length > 1 ? 5 : 4} className="pt-3 text-xs text-muted-foreground">Total</td><td className="pt-3 text-right font-mono-num font-semibold" data-testid={`holdings-total-${accounts[0]}`}>{usd(total, 2)}</td><td /></tr></tfoot>}
        </table>
      </div>
      <AddRow accounts={accounts} onAdded={onChange} />
    </div>
  );
}
